import { Tooltip, Checkbox } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { ColDef, ICellRendererParams, IRowNode } from '@ag-grid-community/core';
import { ValidValuesCache } from 'src/services/validValuesCache';
import { isEqual, isNil, isUndefined, reduce } from 'lodash';
import { ROW_SELECTION_REQUIRED_TEXT, ROW_SELECTIONS_UPDATING_TEXT } from 'src/utils/Domain/Constants';
import { isNotGroupNode } from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import { EditableGridHeaderEditorBaseProps } from './TextValidationHeaderEditor';
import { BatchEdit } from 'src/components/ConfigurableGrid/EditableGrid/EditableGrid';
import { logError } from 'src/services/loggingService';

interface Props extends ICellRendererParams, EditableGridHeaderEditorBaseProps {
  availableSelections: string[];
  groupCheckbox?: boolean;
  validValuesCache: ValidValuesCache;
  columnField: string;
}

const CheckboxHeaderEditor = ({
  availableSelections,
  groupCheckbox,
  getSelectedItems,
  getCellsUpdating,
  column,
  onApplyEdit,
  onApplyBatchEdit,
  columnField,
  api,
}: Props) => {
  const items = getSelectedItems();
  const noItems = getSelectedItems().length === 0;
  const itemsUpdating = getCellsUpdating().length > 0;
  const disabled = noItems || itemsUpdating;
  const [multiOptionCounts, setMultiOptionCounts] = useState<Record<string, number>>({});
  const [singleOptions, setSingleOptions] = useState<boolean | undefined>(false);
  const [toggle, setToggle] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const countOptions = () => {
      if (groupCheckbox) {
        const optionCounts = availableSelections.reduce(
          (counts, option) => {
            counts[option] = 0;
            return counts;
          },
          {} as Record<string, number>,
        );
        // Count occurrences of each option
        items.forEach((item) => {
          const grades: string[] = item.data[columnField] || [];
          grades.forEach((grade) => {
            if (availableSelections.includes(grade)) {
              optionCounts[grade]++;
            }
          });
        });
        if (!isEqual(optionCounts, multiOptionCounts)) {
          setMultiOptionCounts(optionCounts);
        }
      } else {
        const allTrue = items
          .filter(isNotGroupNode)
          .map((n) => api.getValue(column!, n))
          // WARNING: `!!` here coerced to truthy value
          .every((value) => !!value);
        const allFalse = items
          .filter(isNotGroupNode)
          .map((n) => api.getValue(column!, n))
          // WARNING: `!` here coerced to falsy
          .every((value) => !value);
        const newSingleOptionV = allTrue ? true : allFalse ? false : undefined;
        if (newSingleOptionV != singleOptions) {
          setSingleOptions(newSingleOptionV);
        }
      }
    };

    if (!disabled) {
      countOptions();
    }
  }, [api, availableSelections, column, columnField, disabled, groupCheckbox, items, multiOptionCounts, singleOptions]);

  const toggleMultiCheckboxes = (sel: string, checked: boolean) => {
    const colDef = column?.getColDef();
    const dataIndex = column?.getColId() ?? '';

    const nodeObjs = reduce(
      items,
      (acc, node) => {
        let nodeBatchValue: string[] = node.data[columnField] as string[];

        if (checked && !nodeBatchValue.includes(sel)) {
          nodeBatchValue.push(sel);
        } else if (!checked) {
          nodeBatchValue = nodeBatchValue.filter((selection) => selection !== sel);
        }

        setToggle({ ...toggle, [sel]: checked });

        const output: Omit<BatchEdit, 'coordinates'> = {
          dataIndex,
          colDef: colDef!,
          rowNode: node,
          value: nodeBatchValue,
        };
        return [...acc, output];
      },
      [] as unknown as Omit<BatchEdit, 'coordinates'>[],
    );

    if (onApplyBatchEdit) {
      onApplyBatchEdit(nodeObjs);
    } else {
      logError('Unable to process batch edits, missing `onApplyBatchEdit` handler.');
    }
  };

  const toggleSingleSelection = (checked: boolean) => {
    onApplyEdit(checked, items, column?.getColDef());
    setSingleOptions(checked);
  };

  let element: JSX.Element[] | undefined;
  if (groupCheckbox) {
    element = availableSelections.map((sel, _idx, arr) => {
      const width = `${(1 / arr.length) * 100}%`;
      const cursor = disabled ? 'not-allowed' : 'pointer';
      const isChecked = multiOptionCounts[sel] === items.length && items.length !== 0;
      const isIndeterminate = multiOptionCounts[sel] < items.length && multiOptionCounts[sel] > 0;
      const text = noItems ? ROW_SELECTION_REQUIRED_TEXT : itemsUpdating ? ROW_SELECTIONS_UPDATING_TEXT : '';
      return (
        <span key={sel} className={`vv-cell-checkbox`} style={{ width, cursor }}>
          <Tooltip title={text} disableFocusListener={disabled} disableHoverListener={disabled}>
            <Checkbox
              checked={!isUndefined(toggle[sel]) ? toggle[sel] : isChecked}
              indeterminate={isIndeterminate}
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<CheckCircleOutlineIcon />}
              onChange={(event) => toggleMultiCheckboxes(sel, event.target.checked)}
              disabled={disabled}
              disableRipple={!disabled}
            />
          </Tooltip>
        </span>
      );
    });
  } else {
    const text = noItems ? ROW_SELECTION_REQUIRED_TEXT : itemsUpdating ? ROW_SELECTIONS_UPDATING_TEXT : '';
    const cursor = disabled ? 'not-allowed' : 'pointer';
    return (
      <span className={`vv-cell-checkbox`} style={{ cursor, width: '100%', justifyContent: 'center' }}>
        <Tooltip title={text} disableFocusListener={disabled} disableHoverListener={disabled}>
          <Checkbox
            checked={singleOptions}
            indeterminate={singleOptions === undefined}
            icon={<RadioButtonUncheckedIcon />}
            checkedIcon={<CheckCircleOutlineIcon />}
            onChange={(event) => {
              toggleSingleSelection(event.target.checked);
            }}
            disabled={disabled}
            disableRipple={!disabled}
          />
        </Tooltip>
      </span>
    );
  }
  return <div style={{ width: '100%', display: 'flex' }}>{element}</div>;
};

export default CheckboxHeaderEditor;
